import { Play, Pause, SkipBack, SkipForward, X, ListMusic, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useMediaPlayer, spotifyEmbedUrl, mixcloudEmbedUrl, soundcloudEmbedUrl } from "../lib/player";

/**
 * Sticky bottom mini-player. Hidden when nothing is queued.
 * Renders an HTML5 audio bar OR a Spotify embed iframe based on the item kind.
 */
export default function MiniPlayer() {
    const { current, playing, progress, toggle, next, prev, close, seek, queue, index } = useMediaPlayer();
    const [showQueue, setShowQueue] = useState(false);

    if (!current) return null;

    const isSpotify = current.kind === "spotify";
    const isMixcloud = current.kind === "mixcloud";
    const isSoundcloud = current.kind === "soundcloud";
    const isEmbed = isSpotify || isMixcloud || isSoundcloud;

    return (
        <div
            data-testid="mini-player"
            className="fixed bottom-0 left-0 right-0 z-40 bg-streetz-paper border-t-2 border-streetz-red text-white shadow-[0_-8px_28px_rgba(0,0,0,0.55)]"
        >
            {showQueue && queue.length > 1 && (
                <div className="max-w-[1600px] mx-auto px-4 md:px-8 pt-3 pb-2 border-b border-streetz-line">
                    <div className="overline text-streetz-yellow mb-2">UP NEXT · {queue.length - index - 1} TRACKS</div>
                    <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                        {queue.map((q, i) => (
                            <div
                                key={q.id}
                                data-testid={`player-queue-item-${q.id}`}
                                className={`flex items-center gap-3 text-sm py-1.5 px-2 ${
                                    i === index ? "bg-streetz-red/15 text-streetz-yellow" : ""
                                }`}
                            >
                                <span className="overline text-[10px] w-6 text-streetz-muted">{i + 1}</span>
                                <span className="flex-1 truncate">{q.title}</span>
                                {q.host && <span className="overline text-[10px] text-streetz-muted truncate max-w-[140px]">{q.host}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 grid grid-cols-12 items-center gap-3 md:gap-5">
                {/* Track info */}
                <div className="col-span-12 md:col-span-4 flex items-center gap-3 min-w-0">
                    {current.image ? (
                        <img
                            src={current.image}
                            alt={current.title}
                            className="w-24 h-12 object-contain bg-black border border-streetz-line p-1 flex-shrink-0"
                        />
                    ) : (
                        <div className="w-24 h-12 bg-streetz-line grid place-items-center text-streetz-yellow flex-shrink-0">
                            <ListMusic className="w-5 h-5" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-sm font-semibold truncate" data-testid="player-current-title">{current.title}</div>
                        <div className="overline text-[10px] text-streetz-muted truncate">
                            {current.host || current.kind?.toUpperCase()}
                        </div>
                    </div>
                </div>

                {/* Controls + progress (audio only) */}
                <div className="col-span-12 md:col-span-5 flex flex-col items-center gap-1">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={prev}
                            disabled={index === 0}
                            aria-label="Previous"
                            data-testid="player-prev"
                            className="h-9 w-9 grid place-items-center text-white hover:text-streetz-yellow disabled:opacity-40"
                        >
                            <SkipBack className="w-4 h-4" />
                        </button>
                        <button
                            onClick={toggle}
                            aria-label={playing ? "Pause" : "Play"}
                            data-testid="player-toggle"
                            className="h-10 w-10 grid place-items-center bg-streetz-red text-white rounded-full hover:bg-streetz-yellow hover:text-black"
                        >
                            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-[2px]" />}
                        </button>
                        <button
                            onClick={next}
                            disabled={index >= queue.length - 1}
                            aria-label="Next"
                            data-testid="player-next"
                            className="h-9 w-9 grid place-items-center text-white hover:text-streetz-yellow disabled:opacity-40"
                        >
                            <SkipForward className="w-4 h-4" />
                        </button>
                    </div>
                    {!isEmbed && (
                        <div
                            className="w-full h-1 bg-streetz-line cursor-pointer relative"
                            onClick={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                seek((e.clientX - r.left) / r.width);
                            }}
                            data-testid="player-progress"
                        >
                            <div className="absolute inset-y-0 left-0 bg-streetz-yellow" style={{ width: `${progress * 100}%` }} />
                        </div>
                    )}
                </div>

                {/* Right side: queue toggle + close */}
                <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-2">
                    {queue.length > 1 && (
                        <button
                            onClick={() => setShowQueue((v) => !v)}
                            data-testid="player-queue-toggle"
                            aria-label="Toggle queue"
                            className={`overline text-[10px] px-2 py-2 border ${
                                showQueue ? "bg-streetz-yellow text-black border-streetz-yellow" : "border-streetz-line text-white hover:border-streetz-yellow"
                            }`}
                        >
                            <ListMusic className="w-4 h-4 inline -mt-[2px] mr-1" /> QUEUE
                        </button>
                    )}
                    <button
                        onClick={close}
                        aria-label="Close player"
                        data-testid="player-close"
                        className="h-9 w-9 grid place-items-center text-streetz-muted hover:text-streetz-red"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Embeds (Spotify / Mixcloud / SoundCloud) below the controls */}
            {isSpotify && (
                <div className="border-t border-streetz-line bg-black relative">
                    <iframe
                        title="Spotify embed"
                        data-testid="player-spotify-embed"
                        src={spotifyEmbedUrl(current.url)}
                        width="100%"
                        height="152"
                        style={{ border: 0, display: "block" }}
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                    />
                    {/* Always-available fallback for any embed issue (region lock, private item, browser ICU mismatch) */}
                    <a
                        href={current.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="player-spotify-open"
                        className="absolute top-2 right-2 inline-flex items-center gap-1 bg-[#1DB954] text-black overline text-[10px] px-2 py-1 hover:bg-streetz-yellow"
                        title="Open in Spotify"
                    >
                        OPEN IN SPOTIFY <ExternalLink className="w-3 h-3" />
                    </a>
                </div>
            )}
            {isMixcloud && (
                <div className="border-t border-streetz-line bg-black">
                    <iframe
                        title="Mixcloud embed"
                        data-testid="player-mixcloud-embed"
                        src={mixcloudEmbedUrl(current.url)}
                        width="100%"
                        height="60"
                        style={{ border: 0, display: "block" }}
                        allow="autoplay; encrypted-media"
                        loading="lazy"
                    />
                </div>
            )}
            {isSoundcloud && (
                <div className="border-t border-streetz-line bg-black">
                    <iframe
                        title="SoundCloud embed"
                        data-testid="player-soundcloud-embed"
                        src={soundcloudEmbedUrl(current.url)}
                        width="100%"
                        height="120"
                        style={{ border: 0, display: "block" }}
                        allow="autoplay; encrypted-media"
                        loading="lazy"
                    />
                </div>
            )}
        </div>
    );
}
